<template>
  <div v-if="hasQuotes">
    <div>
      <BaseButton
        :text="`Back to 'Property details'`"
        @click="back"
        class="font-weight-bold d-flex ml-n5"
        color="#106175"
        flat
        icon="mdi-chevron-left"
        ripple
        small
      />

      <div class="d-flex justify-space-between mt-10">
        <h1 class="d-none d-lg-block font-lg-28">
          Your Tenants Liability Insurance quote
        </h1>
        <h1 class="d-block d-lg-none">Your quote</h1>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="toggleGlobalSaveAndExitDialog"
        />
      </div>
    </div>

    <div class="d-flex mt-10 align-center justify-space-between">
      <div
        class="d-flex flex-column flex-lg-row flex-wrap gc-5 gr-2"
        v-if="isPremiumFlexVisible"
      >
        <div class="d-flex align-center gc-2">
          <h3 class="d-none d-md-block font-20">Premium flex</h3>
          <h3 class="d-md-none font-20">Flex</h3>
          <BaseTooltip
            :text="TOOL_TIP_TEXT.PremiumFlex"
            icon="mdi-help-circle"
          />
        </div>

        <FlexSpinner
          :initialVal="commissionSacrifice"
          :maxCommissionSacrifice="getMaxCommissionSacrifice"
          @input="updatePrice"
          class="font-18"
          type="percentage"
        />
      </div>
      <div class="d-flex flex-column flex-lg-row justify-flex-end gc-10 gr-4">
        <BaseButton
          @click="shareQuote()"
          class="font-weight-bold px-1 py-3 d-flex"
          color="psdarkgreen"
          depressed
          flat
          icon="mdi-share-variant"
          ripple
          small
          :text="$vuetify.breakpoint.lgAndUp ? 'Share quote' : 'Share'"
        />
        <BaseButton
          @click="printQuote()"
          class="font-weight-bold px-1 py-3 d-flex"
          color="psdarkgreen"
          depressed
          flat
          icon="mdi-printer"
          ripple
          small
          :text="$vuetify.breakpoint.lgAndUp ? 'Print quote' : 'Print'"
        />
      </div>
    </div>

    <v-row no-gutters class="mt-6 gr-4">
      <v-col class="insurance-card rounded-lg">
        <div
          v-if="isSixMonthsCover"
          class="insurance-card-header bggrey justify-center"
        >
          <h4>6 months cover</h4>
        </div>
        <div
          v-else
          class="insurance-card-header bggrey justify-center justify-xl-space-between"
        >
          <h4 class="d-none d-xl-block">12 months cover</h4>
          <PaymentFrequencySwitch
            :false-value="PAYMENT_FREQUENCY_VALUE.MONTHLY"
            :true-value="PAYMENT_FREQUENCY_VALUE.ANNUAL"
            false-label="Monthly"
            true-label="Yearly"
            v-model="paymentFrequency.Value"
          />
        </div>
        <v-divider class="bordergrey"></v-divider>
        <div class="insurance-card-body">
          <h2>Tenants Liability Insurance</h2>
          <div class="min-h-150">
            <p class="my-4 font-18">
              £10,000 of Tenant’s Liability cover and £100,000 of Legal Expenses
              cover
            </p>
            <span class="price">{{ price | formatCurrency }}</span>
            <h4>{{ paymentFrequencyDescription }}</h4>
            <BaseButton
              @click="showBreakdown"
              class="px-0 primary--text opacity-80"
              depressed
              flat
              link
              ripple
              text="View a breakdown"
            />
          </div>
          <BaseButton
            @click="applyForQuote()"
            class="font-20 font-weight-bold py-6 my-6"
            color="mustard"
            depressed
            height="60"
            rounded
            text="APPLY"
            width="100%"
          />
          <WhatsIncluded :items="WHATS_INCLUDED.TENANTS_LIABILITY" />
        </div>
      </v-col>

      <v-col
        cols="12"
        lg="1"
        class="align-self-center text-center text-uppercase"
        v-if="canSellTC"
      >
        <h2>OR</h2></v-col
      >

      <v-col class="insurance-card rounded-lg" v-if="canSellTC">
        <div
          class="insurance-card-header bg-psorange-opacity-50 justify-center"
        >
          <h4>Upgrade your cover</h4>
        </div>
        <v-divider class="bordergrey"></v-divider>
        <div class="insurance-card-body">
          <h2>Tenants Contents Insurance</h2>
          <div class="min-h-150">
            <p class="my-4 font-18">
              Our 5 star Defaqto rated Tenant's Contents Insurance covers your
              own contents while also offering liability insurance as standard,
              meaning tenants are also protected in the event of any accidental
              damage to their landlord's property.
            </p>
          </div>
          <BaseButton
            @click="getQuote"
            class="white--text font-20 font-weight-bold py-6 my-6"
            color="psdarkgreen"
            depressed
            height="60"
            rounded
            text="GET A QUOTE"
            width="100%"
          />
          <WhatsIncluded :items="WHATS_INCLUDED.TENANTS_CONTENTS" />
        </div>
      </v-col>
    </v-row>

    <BaseDialog
      headerIcon="mdi-share-variant"
      headerIconColor="psdarkgreen"
      ref="shareDialog"
    >
      <template v-slot:content="{ data }">
        <DialogContentShareQuotes
          :content="data?.content"
          :quoteId="data?.quoteId"
          :quoteRequestId="data?.quoteRequestId"
          @success="shareQuotesSuccess"
        ></DialogContentShareQuotes>
      </template>
    </BaseDialog>

    <BaseDialog
      headerClass="pa-4"
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPremiumBreakdown
          :breakdown-model="data"
          :insurancePremiumTitle="'Tenants Liability premium (ex IPT)'"
        ></DialogContentPremiumBreakdown>
      </template>
    </BaseDialog>

    <BaseDialog
      contentClass="mb-0"
      headerClass="px-7 pt-6 pb-3"
      headerDivider
      headerIcon="mdi-printer"
      headerIconColor="psdarkgreen"
      ref="printDialog"
      windowClass="pa-0 pb-xl-0 mb-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
      </template>
    </BaseDialog>

    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";

import {
  INTERFACE_KEY,
  PAYMENT_FREQUENCY_VALUE,
  TERM_OF_INSURANCE_VALUE,
} from "@/constants/answer.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import { WHATS_INCLUDED } from "@/constants/whats-included.constants";
import { PRODUCT, TOOL_TIP_TEXT } from "@/constants/common.constants.js";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";

export default {
  name: "YourQuotesPage",
  components: {
    BaseButton: () => import("@/components/BaseComponents/BaseButton.vue"),
    FlexSpinner: () => import("@/components/QuoteResult/FlexSpinner.vue"),
    BaseTooltip: () => import("@/components/BaseComponents/BaseTooltip.vue"),
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    DialogContentShareQuotes: () =>
      import("@/components/QuoteResult/DialogContentShareQuotes.vue"),
    DialogContentPrintQuotes: () =>
      import("@/components/QuoteResult/DialogContentPrintQuotes.vue"),
    DialogContentPremiumBreakdown: () =>
      import("@/components/QuoteResult/DialogContentPremiumBreakdown.vue"),
    WhatsIncluded: () => import("@/components/QuoteResult/WhatsIncluded.vue"),
    PaymentFrequencySwitch: () =>
      import("@/components/QuoteResult/PaymentFrequencySwitch.vue"),
  },
  mixins: [PremiumBreakdownMixin],
  data() {
    return {
      PAYMENT_FREQUENCY_VALUE,
      TOOL_TIP_TEXT,
      WHATS_INCLUDED,
      paymentFrequency: {
        Value: PAYMENT_FREQUENCY_VALUE.ANNUAL,
      },
    };
  },
  created() {
    this.paymentFrequency = this.questionMap[INTERFACE_KEY.PAYMENT_FREQUENCY];
    if (this.isSixMonthsCover || !this.paymentFrequency.Value) {
      this.paymentFrequency.Value = PAYMENT_FREQUENCY_VALUE.ANNUAL;
    }
  },
  mounted() {
    // Can't go forward automatically from this page
    this.setRestoringLastStep(false);
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "getAnswer",
      "getTenantsLiabilityCover",
      "getMaxCommissionSacrifice",
      "hasQuotes",
      "insurerQuotesModel",
      "isAdvisedSeller",
      "termOfInsurance",
      "getQuoteRequest",
      "httpConfig",
      "branchNumber",
    ]),
    ...mapState(QUESTIONS_MODULE, [
      "commissionSacrifice",
      "overlay",
      "quoteResponse",
      "products",
    ]),
    ...mapState(APPLICATION_MODULE, ["questionMap"]),

    isPremiumFlexVisible() {
      return (
        !this.isAdvisedSeller &&
        this.getMaxCommissionSacrifice &&
        this.getMaxCommissionSacrifice !== 0
      );
    },
    isSixMonthsCover() {
      return this.termOfInsurance === TERM_OF_INSURANCE_VALUE.SixMonths;
    },
    isMonthlyPayment() {
      return this.paymentFrequency.Value === PAYMENT_FREQUENCY_VALUE.MONTHLY;
    },
    paymentFrequencyDescription() {
      if (this.isMonthlyPayment) {
        return "Monthly payments";
      }

      return "One off payment";
    },
    quote() {
      return this.insurerQuotesModel[0].selectedQuote;
    },
    price() {
      if (this.isMonthlyPayment) {
        return this.quote.MonthlyTotal;
      }

      return this.quote.AnnualTotal;
    },
    canSellTC() {
      return this.products.some(
        (item) =>
          item.WebProductGroupId === PRODUCT.Tenants_Contents &&
          item.Branches.some((branch) => branch.BranchNo === this.branchNumber)
      );
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "createQuote",
      "getDataToPrintQuote",
      "getDocuments",
      "saveQuoteResults",
      "setOverlayVisible",
      "setRestoringLastStep",
      "updateCommissionSacrifice",
      "toggleGlobalSaveAndExitDialog",
      "refreshSessionAndNextStep",
      "applyForEssentialCover",
    ]),
    ...mapActions(APPLICATION_MODULE, ["setApplicationActiveQuote"]),
    back() {
      this.$router.push({
        name: ROUTE_NAME.TENANTS_LIABILITY.B2B.PROPERTY_DETAILS,
      });
    },
    updatePrice(value) {
      this.updateCommissionSacrifice(value);
    },
    async saveQuotesAndGetDocumentsAsync() {
      await this.saveQuoteResults();

      return await this.getDocuments();
    },
    async shareQuote() {
      try {
        this.setOverlayVisible(true);

        await this.saveQuoteResults();

        this.$refs.shareDialog.open("Share via email", {
          content: this.getTenantsLiabilityCover,
          quoteId: this.quote.QuoteId,
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.setOverlayVisible(false);
      }
    },
    async printQuote() {
      try {
        this.setOverlayVisible(true);

        const docs = await this.saveQuotesAndGetDocumentsAsync();

        var data = await this.getDataToPrintQuote({
          docs,
          quote: this.quote,
          paymentFrequency: this.paymentFrequency.Value,
        });

        this.$refs.printDialog.open("Download for printing", data);
      } catch (error) {
        console.error(error);
      } finally {
        this.setOverlayVisible(false);
      }
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },
    applyForQuote() {
      this.setApplicationActiveQuote(this.insurerQuotesModel[0]);
      this.refreshSessionAndNextStep(
        ROUTE_NAME.TENANTS_LIABILITY.B2B.APPLICATION
      );
    },
    async getQuote() {
      await this.applyForEssentialCover(PRODUCT.Tenants_Contents);
    },
    showBreakdown() {
      if (this.isMonthlyPayment) {
        this.showPremiumBreakdownMonthly(this.quote);
      } else {
        this.showPremiumBreakdownAnnual(this.quote);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.insurance-card {
  border: solid var(--v-bordergrey-base) 1px;
  overflow: hidden;
  max-width: 500px !important;

  .insurance-card-header {
    height: 44px;
    display: flex;
    align-items: center;
    padding: 0 16px;
  }

  .bg-psorange-opacity-50 {
    background: rgba(250, 181, 25, 0.5);
  }

  .insurance-card-body {
    padding: 40px 27px;
  }

  .price {
    font-size: 40px;
    font-weight: 700;
    line-height: 48.76px;
  }
}
</style>
